import {
    createContext,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

interface DisplayedCarouselItemProps<T = any> {
    displayedItems: T[];
    isFetched?: boolean;
    isMHRetriggerShown?: boolean;
}
export interface DisplayedCarouselItemContextValues<T = any> {
    setDisplayedItemIndex: (index: number) => void;
    addVisibleItem: (index: number) => void;
    removeVisibleItem: (index: number) => void;
    displayedItemIndex?: number;
    visibleItemsIndex?: number[];
    displayedItem?: T;
    visibleItems?: T[];
}

export const DisplayedCarouselItemContext = createContext<
    // <any> because generic typing is enforced below
    DisplayedCarouselItemContextValues<any> | undefined
>(undefined);

/**
 * Tracks which carousel items are currently visible in the viewport and which
 * item is "active" (displayed). Centralizes intersection-observer state so
 * consumers don't each need to reimplement it.
 *
 * Primary consumers:
 * - Nav arrows: read `visibleItemsIndex` to disable back/forward at the ends
 * - Carousel list items: call `addVisibleItem` / `removeVisibleItem` as they
 *   enter or leave the viewport
 *
 * `isMHRetriggerShown` — when true, the carousel renders an extra injected
 * item at index 0 that is not part of `displayedItems`, so all data lookups
 * are offset by -1 to stay aligned with the correct record.
 */
export function DisplayedCarouselItemProvider<T = any>({
    displayedItems,
    isFetched,
    isMHRetriggerShown = false,
    children,
}: PropsWithChildren & DisplayedCarouselItemProps<T>) {
    const [displayedItem, setDisplayedItem] = useState<T>();
    // The index is needed since some carousels manually add the "view all clinical programs" that is not part of the displayedItems array. This leads to the displayedItem not changing and doesn't trigger the change to the consumer of this context. The index state change will allow the change to be detected.
    const [displayedItemIndex, setDisplayedItemIndex] = useState<number>();
    const [visibleItemsIndex, setVisibleItemsIndex] = useState<number[]>([]);

    const setDisplayedItemIndexIntercept = useCallback(
        (index: number) => {
            setDisplayedItemIndex(index);
            if (isFetched && !!displayedItems?.length) {
                setDisplayedItem(
                    displayedItems[isMHRetriggerShown ? index - 1 : index]
                );
            }
        },
        [isFetched, displayedItems, isMHRetriggerShown]
    );

    const addVisibleItem = useCallback((index: number) => {
        setVisibleItemsIndex((prev) => [...prev, index]);
    }, []);

    const removeVisibleItem = useCallback((index: number) => {
        setVisibleItemsIndex((prev) => prev.filter((i) => i !== index));
    }, []);

    const visibleItems = useMemo(
        () =>
            visibleItemsIndex
                .map((i) => displayedItems[isMHRetriggerShown ? i - 1 : i])
                .filter(Boolean),
        [visibleItemsIndex, displayedItems, isMHRetriggerShown]
    );

    useEffect(() => {
        if (
            isFetched &&
            displayedItems &&
            !displayedItem &&
            !isMHRetriggerShown
        ) {
            setDisplayedItem(displayedItems[0]);
        }
    }, [isFetched, displayedItems, displayedItem, isMHRetriggerShown]);

    const state = useMemo(
        () => ({
            displayedItem,
            setDisplayedItemIndex: setDisplayedItemIndexIntercept,
            displayedItemIndex,
            addVisibleItem,
            removeVisibleItem,
            visibleItems,
            visibleItemsIndex,
        }),
        [
            displayedItem,
            setDisplayedItemIndexIntercept,
            displayedItemIndex,
            addVisibleItem,
            removeVisibleItem,
            visibleItems,
            visibleItemsIndex,
        ]
    );

    return (
        <DisplayedCarouselItemContext.Provider value={state}>
            {children}
        </DisplayedCarouselItemContext.Provider>
    );
}
